import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ReleaseStateChip from './ReleaseStateChip';
import { ROUTES } from './routes';

const utilityItems = [
  { label: 'Templates', to: ROUTES.templates, icon: <CollectionsBookmarkOutlinedIcon /> },
  { label: 'AI Import', to: ROUTES.journeyImport, icon: <FileUploadOutlinedIcon /> },
];

const guideItems = [
  { label: 'Use with ChatGPT Plus', to: ROUTES.waysToUseChatGpt },
  { label: 'Use the Web App', to: ROUTES.waysToUseWebApp },
];

export default function ButtonAppBar() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [utilitiesMenuAnchor, setUtilitiesMenuAnchor] = React.useState(null);
  const [waysMenuAnchor, setWaysMenuAnchor] = React.useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = React.useState(null);
  const { authState, logout } = useAuth();
  const currentUser = authState.user;
  const isLoggedIn = Boolean(currentUser);
  const userIdentity = currentUser?.email || currentUser?.displayName || 'Signed-in user';
  const userDisplayName = authState.profile?.name || currentUser?.displayName || userIdentity;
  const avatarInitial = isLoggedIn ? userDisplayName.trim().charAt(0).toUpperCase() : '';

  const closeDrawer = () => setDrawerOpen(false);
  const closeAccountMenu = () => setAccountMenuAnchor(null);
  const handleLogout = async () => {
    closeAccountMenu();
    await logout();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="sticky" data-testid="app-header">
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="Open navigation"
            data-testid="mobile-nav-open"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component={RouterLink}
            to={ROUTES.root}
            data-testid="app-title-link"
            sx={{ flexGrow: 1, textAlign: 'left', color: 'inherit', textDecoration: 'none' }}
          >
            Journey Planner
          </Typography>

          <Box
            component="nav"
            aria-label="Primary navigation"
            data-testid="desktop-primary-navigation"
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}
          >
            <Button
              component={RouterLink}
              to={ROUTES.mainView}
              color="inherit"
              size="small"
              startIcon={<ViewColumnOutlinedIcon />}
              data-testid="nav-journey-board"
            >
              Journey Board
            </Button>
            <Button
              color="inherit"
              size="small"
              startIcon={<HandymanOutlinedIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(event) => setUtilitiesMenuAnchor(event.currentTarget)}
              aria-controls={utilitiesMenuAnchor ? 'utilities-menu' : undefined}
              aria-haspopup="menu"
              aria-expanded={Boolean(utilitiesMenuAnchor)}
              data-testid="nav-utilities"
            >
              Utilities
            </Button>
            <Menu
              id="utilities-menu"
              anchorEl={utilitiesMenuAnchor}
              open={Boolean(utilitiesMenuAnchor)}
              onClose={() => setUtilitiesMenuAnchor(null)}
            >
              {utilityItems.map((item) => (
                <MenuItem
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  onClick={() => setUtilitiesMenuAnchor(null)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Menu>
            <Button
              color="inherit"
              size="small"
              startIcon={<HelpOutlineIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(event) => setWaysMenuAnchor(event.currentTarget)}
              aria-controls={waysMenuAnchor ? 'ways-to-use-menu' : undefined}
              aria-haspopup="menu"
              aria-expanded={Boolean(waysMenuAnchor)}
              data-testid="nav-ways-to-use"
            >
              Ways to Use
            </Button>
            <Menu
              id="ways-to-use-menu"
              anchorEl={waysMenuAnchor}
              open={Boolean(waysMenuAnchor)}
              onClose={() => setWaysMenuAnchor(null)}
            >
              {guideItems.map((item) => (
                <MenuItem
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  onClick={() => setWaysMenuAnchor(null)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
            <Button
              color="inherit"
              component={RouterLink}
              to={ROUTES.feedback}
              size="small"
              startIcon={<MailOutlineIcon />}
              data-testid="nav-feedback"
            >
              Feedback
            </Button>
          </Box>

          <Box sx={{ display: isLoggedIn ? 'flex' : 'none', flexShrink: 0, ml: { md: 0.5 } }}>
            <ReleaseStateChip visible={isLoggedIn} />
          </Box>

          <IconButton
            color="inherit"
            aria-label={isLoggedIn ? `Open account menu for ${userIdentity}` : 'Open account menu; not signed in'}
            aria-controls={accountMenuAnchor ? 'account-menu' : undefined}
            aria-haspopup="menu"
            aria-expanded={Boolean(accountMenuAnchor)}
            onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
            data-testid="account-menu-button"
            sx={{ ml: 0.5, p: 0.35 }}
          >
            <Avatar
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                bgcolor: isLoggedIn ? 'secondary.main' : 'rgba(255, 255, 255, 0.12)',
                color: 'common.white',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                fontSize: '1rem',
                fontWeight: 800,
              }}
            >
              {avatarInitial || <PersonOutlineIcon fontSize="small" />}
            </Avatar>
          </IconButton>
          <Menu
            id="account-menu"
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={closeAccountMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 288, maxWidth: 'calc(100vw - 24px)', mt: 1 } } }}
          >
            <Box sx={{ px: 2, py: 1.25 }} data-testid="auth-status">
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
                User profile
              </Typography>
              <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                {isLoggedIn ? userDisplayName : 'Guest'}
              </Typography>
              {isLoggedIn && userDisplayName !== userIdentity ? (
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {userIdentity}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Login status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {isLoggedIn ? 'Signed in' : 'Not signed in'}
              </Typography>
            </Box>
            <Divider />
            {isLoggedIn ? (
              <MenuItem onClick={handleLogout} data-testid="nav-logout">
                <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Log out" />
              </MenuItem>
            ) : (
              <MenuItem component={RouterLink} to={ROUTES.login} onClick={closeAccountMenu} data-testid="nav-login">
                <ListItemIcon><LoginOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Login" />
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 280 }} role="navigation" onClick={closeDrawer}>
            <Typography variant="h6" sx={{ px: 2, py: 2 }}>
              Journey Planner
            </Typography>
          {isLoggedIn ? (
            <Box sx={{ px: 2, pb: 1 }}>
              <ReleaseStateChip visible />
            </Box>
          ) : null}
          <Divider />
          <List component="nav" aria-label="Mobile primary navigation">
            <ListItemButton component={RouterLink} to={ROUTES.mainView} data-testid="mobile-nav-journey-board">
              <ListItemIcon><ViewColumnOutlinedIcon /></ListItemIcon>
              <ListItemText primary="Journey Board" />
            </ListItemButton>
            <ListSubheader component="div" data-testid="mobile-nav-utilities">Utilities</ListSubheader>
            {utilityItems.map((item) => (
              <ListItemButton key={item.to} component={RouterLink} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} sx={{ pl: 3 }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <ListSubheader component="div" data-testid="mobile-nav-ways-to-use">Ways to Use</ListSubheader>
            {guideItems.map((item) => (
              <ListItemButton key={item.to} component={RouterLink} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} sx={{ pl: 3 }}>
                <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <Divider component="li" sx={{ my: 1 }} />
            <ListItemButton component={RouterLink} to={ROUTES.feedback} data-testid="mobile-nav-feedback">
              <ListItemIcon><MailOutlineIcon /></ListItemIcon>
              <ListItemText primary="Feedback" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
